import { QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';

import App from '@/App';
import '@/index.css';
import ErrorFallback from '@/components/ErrorFallback';
import { queryClient } from '@/lib/queryClient';
import { initGlobalErrorHandling, logError, initConsoleInterception } from '@/utils/errorLogger';
import { SentryErrorBoundary } from '@/utils/sentry';
import { initSentry } from '@/utils/sentry-utils';

/**
 * Browser-specific initialization
 * Only runs in browser environment, safely handles SSR
 */
const initializeBrowserEnvironment = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Initialize console interception first to catch early messages
  try {
    initConsoleInterception();
  } catch (error) {
    console.warn('Failed to initialize console interception:', error as Error);
  }

  // Initialize Sentry before other error handling
  initSentry();

  // Initialize global error handling
  initGlobalErrorHandling();
};

/**
 * Renders the React application to the DOM
 */
const renderApp = (): void => {
  const container = document.querySelector('#root');

  if (!container) {
    logError(new Error('Root container not found'));
    return;
  }

  try {
    const root = ReactDOM.createRoot(container);
    root.render(
      <SentryErrorBoundary fallback={<ErrorFallback />}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </SentryErrorBoundary>
    );
  } catch (error) {
    logError(error as Error, { source: 'Failed to render app' });
  }
};

/**
 * Application bootstrap
 */
const bootstrap = (): void => {
  initializeBrowserEnvironment();

  if (typeof window !== 'undefined') {
    renderApp();
  }
};

// Start the application
bootstrap();

// React Fast Refresh handles hot reloading automatically - no manual HMR needed
